import { useTranslation } from '@egw/i18n'
import cn from 'classnames'
import { type FC, useEffect, useRef } from 'react'
import { type BankAccountDetail, EgmCardStatus, useCustomerDetails } from '@egw/api'
import { Logger } from '@egw/logger'
import { Spinner } from '@egw/components'
import CardFront from '../card-front/card-front'
import { ECardDetail, useVgsShow } from '../../context/vsg-context-provider'
import { card_number_styles, cvv_styles, exp_date_styles } from './iframeStyles'
import styles from './styles.module.css'

const log = new Logger('Card')

interface CardViewProps {
  isCardInactive?: boolean
  // Account Closed State
  cardDisabled?: boolean
  cardDetails?: BankAccountDetail
}

const CardView: FC<CardViewProps> = ({ isCardInactive, cardDisabled = false, cardDetails }: CardViewProps) => {
  // -- Refs --------
  const cardNumberRef = useRef<HTMLDivElement | null>(null)
  const expRef = useRef<HTMLDivElement | null>(null)
  const cvvRef = useRef<HTMLDivElement | null>(null)

  // -- Hooks ---------------------------------------------------------------
  const { renderCardValue, cardRevealed, showCard, isLoading, toggleShowCard, showError, anyInitial } = useVgsShow()

  const { data: customerData } = useCustomerDetails()

  const card = cardDetails?.debitCard[0]

  const cardData = {
    card,
    needsPin: card?.needToSetPin,
    cardIsActivated: card?.cardStatus === EgmCardStatus.ACTIVE,
    cardId: card?.cardId,
    accountName: cardDetails?.accountName,
    cardStatus: card?.cardStatus,
    debitCardPresent: Boolean(card),
    accountClosedDate: Boolean(card?.cardStatus === EgmCardStatus.CLOSED),
    alias: cardDetails?.alias,
  }

  const { t } = useTranslation()

  // -- Effects ---------------------------------------------------------------
  // Calls VGS to get PCI data into iframes
  useEffect(() => {
    const hasCardData = cardData.debitCardPresent
    // log.info({ cardNumberRef, expRef, cvvRef })
    if (showCard && !cardRevealed && !isLoading && renderCardValue && hasCardData) {
      // In future this will need refactoring to support multiple cards

      renderCardValue({
        wrapper: cardNumberRef,
        // eslint-disable-next-line camelcase -- this is a prop
        styles: card_number_styles,
        cardId: cardData.cardId ?? '',
        jsonPath: 'json.card_number',
        path: '/api/secure/card/info',
        cardDetail: ECardDetail.CARD_NUMBER,
      }).catch((e: unknown) => {
        log.error('Error in rendering card number', e)
      })

      renderCardValue({
        wrapper: expRef,
        // eslint-disable-next-line camelcase -- this is a prop
        styles: exp_date_styles,
        cardId: cardData.cardId ?? '',
        jsonPath: 'json.exp',
        path: '/api/secure/card/info',
        cardDetail: ECardDetail.EXPIRATION_DATE,
      }).catch((e: unknown) => {
        log.error('Error in rendering card number', e)
      })

      renderCardValue({
        wrapper: cvvRef,
        // eslint-disable-next-line camelcase -- this is a prop
        styles: cvv_styles,
        cardId: cardData.cardId ?? '',
        jsonPath: 'json.cvv',
        path: '/api/secure/card/cvv',
        cardDetail: ECardDetail.CVV,
        payload: { expirationDate: cardData.card?.expDate! },
      }).catch((e: unknown) => {
        log.error('Error in rendering card number', e)
      })
      return
    }
    if (anyInitial || isLoading) {
      log.warn('Rendering in flight')
    }
    if (showError) log.error('Error in showing card details')
  }, [cardData, cardRevealed, renderCardValue, isLoading, cardNumberRef, showCard, showError, anyInitial])

  // -- Layout ---------------------------------------------------------------
  return (
    <div className="align-center flex w-full justify-center bg-gradient-to-t from-[#C9D3CF] to-[#E9EDEB]">
      <button
        id="revealCardButton"
        type="button"
        className={cn(styles.card, {
          [styles.revealed]: cardRevealed,
        })}
        disabled={isLoading || cardDisabled}
        onClick={toggleShowCard}
      >
        <div className={styles.card_inner}>
          <div
            className={cn(styles.card_front, {
              'opacity-50': isCardInactive ?? isLoading,
            })}
          >
            {isLoading ? (
              <div className="absolute">
                <Spinner className="w-8 h-8 text-gray-400 animate-spin dark:white fill-white" />
              </div>
            ) : null}
            <CardFront width={340} className="mx-4" />
          </div>
          <div className={styles.card_back}>
            <div
              className={cn(styles.card_back_inner, 'rounded-lg bg-black pt-1.5 relative', {
                'opacity-50': isCardInactive,
              })}
            >
              <div className="text-[5px] text-gray-600 ml-6 mb-1.5 text-left">{t('card.issued_by_coastal')}</div>
              <div className="h-8 w-full bg-zinc-800" />
              <dl className="p-6">
                <dt className="text-xs uppercase text-left">
                  {customerData?.customer.name.firstName} {customerData?.customer.name.lastName}{' '}
                </dt>
                <dt className="text-2xl">
                  <div style={{ height: '30px' }} ref={cardNumberRef} />
                </dt>
                <dt className="flex flex-row text-xs">
                  <div className="mt-3 flex flex-row overflow-hidden">
                    <div className="text-[9px] text-left uppercase leading-[.55rem]">
                      exp
                      <br />
                      date
                    </div>
                    <div className="ml-2 text-xl leading-[1rem]" ref={expRef} />
                  </div>
                  <div className="ml-10 mt-3 flex flex-row overflow-hidden">
                    <div className="text-[9px] text-left uppercase leading-[.55rem]">
                      sec
                      <br />
                      code
                    </div>
                    <div className="ml-2 text-xl leading-[1rem]" ref={cvvRef} />
                  </div>
                </dt>
              </dl>
              <CardLogo />
            </div>
          </div>
        </div>
      </button>
    </div>
  )
}

export default CardView

const CardLogo = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      height={20}
      width={60}
      viewBox="0 0 162 22"
      className="fill-zinc-600 absolute bottom-3 left-6"
    >
      <g width={80}>
        <path d="M0 19.547V.453h11.412l1.066 3.143H3.519v4.796h6.323l1.037 3.143H3.52v4.854h7.921l1.038 3.143H0v.015ZM36.41 19.547V.453h11.412l1.065 3.143H39.93v4.796h6.323l1.037 3.143h-7.346v4.854h7.92l1.038 3.143H36.424l-.014.015ZM111.234 19.547V.453h11.412l1.066 3.143h-8.959v4.796h6.323l1.037 3.143h-7.346v4.854h7.921l1.038 3.143h-12.478l-.014.015ZM128.633 19.547V.453h11.412l1.065 3.143h-8.958v4.796h6.323l1.037 3.143h-7.346v4.854h7.921l1.037 3.143h-12.477l-.014.015ZM26.19 19.547h-4.333L15.352.453h3.687l5.005 15.336L29.05.453h3.688l-6.562 19.094h.014ZM57.383 19.547h-3.575V.453h6.422c4.626 0 7.093 2.295 7.093 5.804 0 2.72-1.457 4.606-3.953 5.497l4.809 7.793h-4.066l-4.066-6.989-2.636.19v6.813l-.028-.014Zm2.636-9.81c2.201-.117 3.631-1.155 3.631-3.231 0-2.076-1.43-2.939-3.981-2.939h-2.314v6.228l2.664-.058ZM80.685 20c-5.622 0-9.52-3.757-9.52-9.956S75.035 0 80.713 0c2.229 0 4.837.614 6.477 1.652l-1.136 3.231c-1.612-1.038-3.463-1.681-5.271-1.681-3.632 0-6 2.602-6 6.725 0 4.123 2.256 6.93 6 6.93 1.29 0 2.551-.337 3.449-.76v-3.67h-3.758l-1.051-3.143h8.117v8.918C85.577 19.357 82.998 20 80.685 20ZM96.33 19.547h-3.574V.453h6.42c4.627 0 7.095 2.295 7.095 5.804 0 2.72-1.458 4.606-3.954 5.497l4.809 7.793h-4.066l-4.066-6.989-2.635.19v6.813l-.028-.014Zm2.636-9.81c2.202-.117 3.632-1.155 3.632-3.231 0-2.076-1.43-2.939-3.982-2.939h-2.313v6.228l2.663-.058ZM158.593.453h3.393v19.27h-3.813l-8.763-14.05v13.86h-3.393V.452h3.898l8.664 13.904V.453h.014Z" />
      </g>
    </svg>
  )
}
